import { compactFlatten, deepStripNulls, eq, isNumber, isString, merge, object, SparseNestedArray } from '@art-suite/art-core-ts';

import { normalizeScanOrScanTemplatePartShallow } from '../commonLib';
import { Scope } from '../OptimizedValueExpressions';
import { getIdsByDepth, IdsByDepth, SCAN_PART_DEPTH, ScanAudio, ScanPart, ScanPhoto, ScanValue, ScanValues, ScanVideo } from '../ScanRoot';
import {
  isCalculatableValue,
  ScanTemplateValue,
  ScanTemplateValues,
} from '../ScanTemplateRoot';
import { getOptimizedScanPart } from './getOptimizedScan';
import {
  getScanGroup,
  getScanLineItemWithParents,
  getScanOptionWithParents,
  getScanPartType,
  getScanTaskWithParents,
  getScopeForScanPartChildren,
} from './ScanOptimizedGetters';
import {
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from './ScanRootOptimizedSchemas';

const EMPTY_SCOPE: Scope = {};

/**
 * Like Partial<T> but allows all fields to be undefined or null
 */
export type PartialNullable<T> = {
  [P in keyof T]?: T[P] | null | undefined;
};

// **************************************************************
// Pure Functional Scan Updates (always returns new objects - or same object if no change)
// **************************************************************

const getNormalizedScanValue = (value: ScanValue, scanValueTemplate: ScanTemplateValue): ScanValue => {
  if (scanValueTemplate === undefined) {
    // Nothing to normalize against.
    return value;
  }

  let setValue = value.setValue;

  // Special handling for checkbox type
  if (scanValueTemplate.type === 'checkbox') {
    return {
      calculatedValue: value.calculatedValue,
      setValue: typeof value.setValue === 'boolean' ? value.setValue : undefined,
    };
  }

  // Handle numeric values
  if (isCalculatableValue(scanValueTemplate) && !isNumber(setValue)) {
    setValue = isString(setValue) ? (isNaN(parseFloat(setValue)) ? undefined : parseFloat(setValue)) : undefined;
  }

  return setValue !== value.setValue
    ? setValue == null ? { calculatedValue: value.calculatedValue } : { calculatedValue: value.calculatedValue, setValue }
    : value;
};

const getNormalizedScanValues = (values: ScanValues, templateValues: ScanTemplateValues): ScanValues =>
  object(values, (value, key) => getNormalizedScanValue(value, templateValues[key]));

const getUpdatedScanPartProps = <T extends ScanPartOptimized>(
  current: T,
  updates: PartialNullable<T>,
  depth: number,
  idsByDepth: IdsByDepth,
  scope: Scope,
  now: number = Date.now(),
): T => {
  const result = merge(current, updates) as T;

  // note, merge already takes care of updates.values being null
  if (updates.values && current.template.values) {
    const newValues = getNormalizedScanValues(
      current.values
        ? merge(current.values, updates.values)
        : updates.values,
      current.template.values,
    );
    result.values = eq(newValues, current.values) ? current.values : newValues;
  }

  // remove null / undefined values from result
  for (const key in result) {
    if (result[key] == null) {
      delete result[key];
    }
  }

  if (eq(result, current)) return current; // no change, return the same instance, don't set updatedAt

  result.updatedAt = current.updatedAt ? (now > current.updatedAt ? now : current.updatedAt + 1) : now;

  if (current.customerMetadata || updates.customerMetadata) {
    result.customerMetadata = deepStripNulls(merge(result.customerMetadata, updates.customerMetadata)) as any;
  }

  const withOptimizedUpdates = getOptimizedScanPart(undefined, result.template, result, depth, idsByDepth, scope, current);
  return normalizeScanOrScanTemplatePartShallow(withOptimizedUpdates) as T;
};

export const getUpdatedLineItem = (
  groupId: string,
  taskId: string,
  optionId: string,
  lineItem: ScanLineItemOptimized,
  updates: PartialNullable<ScanLineItemOptimized>,
  scopeForLineItem: Scope,
  now?: number,
): ScanLineItemOptimized =>
  getUpdatedScanPartProps(
    lineItem,
    updates,
    SCAN_PART_DEPTH.lineItem,
    getIdsByDepth(groupId, taskId, optionId, lineItem.id),
    scopeForLineItem,
    now,
  ) as ScanLineItemOptimized;

export const getUpdatedOption = (
  groupId: string,
  taskId: string,
  option: ScanOptionOptimized,
  updates: PartialNullable<ScanOptionOptimized>,
  scopeForOption: Scope,
  now?: number,
): ScanOptionOptimized =>
  getUpdatedScanPartProps(
    option,
    updates,
    SCAN_PART_DEPTH.option,
    getIdsByDepth(groupId, taskId, option.id),
    scopeForOption,
    now,
  ) as ScanOptionOptimized;

export const getUpdatedTask = (
  groupId: string,
  task: ScanTaskOptimized,
  updates: PartialNullable<ScanTaskOptimized>,
  scopeForTask: Scope,
  now?: number,
): ScanTaskOptimized =>
  getUpdatedScanPartProps(
    task,
    updates,
    SCAN_PART_DEPTH.task,
    getIdsByDepth(groupId, task.id),
    scopeForTask,
    now,
  ) as ScanTaskOptimized;

export const getUpdatedGroup = (
  group: ScanGroupOptimized,
  updates: PartialNullable<ScanGroupOptimized>,
  scopeForGroup: Scope,
  now?: number,
): ScanGroupOptimized =>
  getUpdatedScanPartProps(
    group,
    updates,
    SCAN_PART_DEPTH.group,
    getIdsByDepth(group.id),
    scopeForGroup,
    now,
  ) as ScanGroupOptimized;

/**
 * @deprecated Use getUpdatedGroup instead
 */
export const getUpdatedGroupV2 = getUpdatedGroup;

export const getUpdatedScan = (
  scan: ScanRootOptimized,
  updates: PartialNullable<ScanRootOptimized>,
  now?: number,
): ScanRootOptimized =>
  getUpdatedScanPartProps(
    scan,
    updates,
    SCAN_PART_DEPTH.scan,
    getIdsByDepth(),
    EMPTY_SCOPE,
    now,
  ) as ScanRootOptimized;

// **************************************************************
// Get Scan With... Updated Group, Task, Option, Line Item
// **************************************************************
export const getScanWithUpdatedLineItem = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  lineItemId: string,
  updates: Partial<ScanLineItemOptimized>,
  now: number = Date.now(),
): ScanRootOptimized => {
  const { lineItem, option } = getScanLineItemWithParents(scan, groupId, taskId, optionId, lineItemId);

  const scopeForLineItem = getScopeForScanPartChildren(option);
  const updatedLineItem = getUpdatedLineItem(groupId, taskId, optionId, lineItem, updates, scopeForLineItem, now);
  if (lineItem === updatedLineItem) return scan; // no change, return the same instance

  return getScanWithUpdatedOption(
    scan,
    groupId,
    taskId,
    optionId,
    { lineItems: option.lineItems?.map(li => li.id === lineItemId ? updatedLineItem : li) },
    now,
  );
};

export const getScanWithUpdatedOption = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  updates: Partial<ScanOptionOptimized>,
  now: number = Date.now(),
): ScanRootOptimized => {
  const { option, task } = getScanOptionWithParents(scan, groupId, taskId, optionId);

  const updatedOption = getUpdatedOption(groupId, taskId, option, updates, getScopeForScanPartChildren(task), now);
  if (option === updatedOption) return scan; // no change, return the same instance

  return getScanWithUpdatedTask(
    scan,
    groupId,
    taskId,
    { options: task.options?.map(o => o.id === optionId ? updatedOption : o) },
    now,
  );
};

export const getScanWithUpdatedTask = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  updates: Partial<ScanTaskOptimized>,
  now: number = Date.now(),
): ScanRootOptimized => {
  const { task, group } = getScanTaskWithParents(scan, groupId, taskId);

  const updatedTask = getUpdatedTask(groupId, task, updates, getScopeForScanPartChildren(group), now);
  if (task === updatedTask) return scan; // no change, return the same instance

  return getScanWithUpdatedGroup(
    scan,
    groupId,
    { tasks: group.tasks?.map(t => t.id === taskId ? updatedTask : t) ?? [] },
    now,
  );
};

export const getScanWithUpdatedGroup = (
  scan: ScanRootOptimized,
  groupId: string,
  updates: Partial<ScanGroupOptimized>,
  now: number = Date.now(),
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);

  const updatedGroup = getUpdatedGroup(group, updates, getScopeForScanPartChildren(scan), now);
  if (group === updatedGroup) return scan; // no change, return the same instance

  return getUpdatedScan(
    scan,
    { groups: scan.groups?.map(g => g.id === groupId ? updatedGroup : g) },
    now,
  );
};

/**
 * Adds a photo to the scan part, ensuring that the photo is not already in the list (and cleans up duplicates)
 * @param photosOrPhotoIds - args are a SparseNestedArray of ScanPhotos or photoId strings
 * @returns The unique photos
 */
export const getUniqueScanPhotos = (...photosOrPhotoIds: SparseNestedArray<string | ScanPhoto>): ScanPhoto[] => {
  const uniquePhotoIds = new Set();
  const oldPhotos = [...compactFlatten(photosOrPhotoIds)].reverse();
  const newPhotos: ScanPhoto[] = [];
  for (const p of oldPhotos) {
    const photoId = isString(p) ? p : p.photoId;
    if (!uniquePhotoIds.has(photoId)) {
      newPhotos.push(isString(p) ? { photoId } : p);
    }
    uniquePhotoIds.add(photoId);
  }
  return newPhotos.reverse();
};

/**
 * Adds audio to the scan part, ensuring entries are not duplicated by `audioId`.
 * @param audioEntries - args are a SparseNestedArray of ScanAudio
 * @returns The unique audio entries (later duplicates win, same as getUniqueScanPhotos)
 */
export const getUniqueScanAudio = (...audioEntries: SparseNestedArray<ScanAudio>): ScanAudio[] => {
  const uniqueAudioIds = new Set<string>();
  const oldAudio = [...compactFlatten(audioEntries)].reverse();
  const newAudio: ScanAudio[] = [];
  for (const entry of oldAudio) {
    if (!uniqueAudioIds.has(entry.audioId)) {
      newAudio.push(entry);
    }
    uniqueAudioIds.add(entry.audioId);
  }
  return newAudio.reverse();
};

/**
 * Appends a video segment (a ScanVideo referencing the source walkthrough with a `videoOffset`
 * time range) to a group/task `videos[]`, deduping on the source video id + offset range. The
 * segment stays "offsets-only" (videoId points at the source walkthrough) until a clip is materialized.
 * Never mutates `videos`. Returns the same array when the segment is already there.
 * @param videos The existing list of ScanVideo segments
 * @param video The new ScanVideo segment to append
 * @returns The updated list of ScanVideo segments with the new segment appended if it was unique
 */
export const appendUniqueVideoSegment = (
  videos: ScanVideo[] | undefined,
  video: ScanVideo,
): ScanVideo[] => {
  videos = videos ?? [];
  if (
    videos.some(existing =>
      existing.videoId === video.videoId
      && existing.videoOffset?.startSeconds === video.videoOffset?.startSeconds
      && existing.videoOffset?.endSeconds === video.videoOffset?.endSeconds
    )
  ) {
    return videos;
  }
  return [...videos, video];
};

/** Append a pipeline field-extract ScanAudio attachment on ScanRoot (walkthrough-level). */
export const appendFieldExtractScanAudioToScanRoot = (
  scanRoot: ScanRootOptimized,
  params: {
    audioId: string;
    scanVideoId: string;
    endSeconds: number;
    addedAt?: number;
    addedByUserId?: string;
  },
): ScanRootOptimized =>
  getUpdatedScan(scanRoot, {
    audio: getUniqueScanAudio(scanRoot.audio, {
      audioId: params.audioId,
      scanVideoId: params.scanVideoId,
      audioSource: 'fieldExtract',
      audioOffset: { startSeconds: 0, endSeconds: params.endSeconds },
      addedAt: params.addedAt ?? Date.now(),
      addedByUserId: params.addedByUserId,
    }),
  });

/**
 * Single-pass dedup of a ScanPhoto[] by `photoId`. Keeps the FIRST occurrence
 * of each photoId and drops every later duplicate. Returns the same array
 * reference when there are no duplicates.
 */
export const removeDuplicateScanPhotos = (photos: ScanPhoto[]): ScanPhoto[] => {
  const seen = new Set<string>();
  const result: ScanPhoto[] = [];
  let hasDuplicate = false;
  for (const photo of photos) {
    if (seen.has(photo.photoId)) {
      hasDuplicate = true;
      continue;
    }
    seen.add(photo.photoId);
    result.push(photo);
  }
  return hasDuplicate ? result : photos;
};

/**
 * True if the ScanPart's `photos` array contains any duplicate `photoId`s.
 * Works on any ScanPart (ScanRoot, ScanGroup, ScanTask, ScanOption, ScanLineItem).
 */
export const scanPartHasDuplicatePhotos = (part: ScanPart): boolean => {
  const photos = part.photos;
  if (!photos || photos.length < 2) return false;
  const seen = new Set<string>();
  for (const photo of photos) {
    if (seen.has(photo.photoId)) return true;
    seen.add(photo.photoId);
  }
  return false;
};

/**
 * Returns a new ScanPart with duplicate photos removed (first occurrence of
 * each `photoId` kept). Returns the same reference when there are no
 * duplicates. Works on any ScanPart subtype.
 */
export const removeDuplicatePhotosFromScanPart = <T extends ScanPart>(part: T): T => {
  const photos = part.photos;
  if (!photos || photos.length < 2) return part;
  const deduped = removeDuplicateScanPhotos(photos);
  if (deduped === photos) return part;
  return { ...part, photos: deduped };
};

export const getScanPhotosWithoutPhotoId = (scanPhotos: ScanPhoto[] | undefined, photoId: string): ScanPhoto[] | undefined => {
  if (!scanPhotos) return undefined;
  return scanPhotos.filter(p => p.photoId !== photoId);
};

export const getLatestScanPhoto = (scanPhotos: ScanPhoto[] | undefined): ScanPhoto | undefined => {
  if (!scanPhotos) return undefined;
  let latestPhoto: ScanPhoto | undefined;
  for (const photo of scanPhotos) {
    if (
      !latestPhoto
      || (photo.addedAt && latestPhoto.addedAt == null)
      || (photo.addedAt && latestPhoto.addedAt && photo.addedAt > latestPhoto.addedAt)
    ) {
      latestPhoto = photo;
    }
  }
  return latestPhoto;
};

export const getLatestScanPhotos = (scanPhotos: ScanPhoto[] | undefined, maxCount: number): ScanPhoto[] => {
  if (!scanPhotos || scanPhotos.length === 0) return [];
  const startIndex = Math.max(0, scanPhotos.length - maxCount);
  return scanPhotos.slice(startIndex);
};

export const getScanWithUpdatedScanPart = (
  scan: ScanRootOptimized,
  groupId: string | undefined,
  taskId: string | undefined,
  optionId: string | undefined,
  lineItemId: string | undefined,
  updates: Partial<ScanPartOptimized>,
): ScanRootOptimized => {
  switch (getScanPartType(groupId, taskId, optionId, lineItemId)) {
    case 'scan':
      return getUpdatedScan(scan, updates);
    case 'group':
      if (!groupId) throw new Error('groupId is required');
      return getScanWithUpdatedGroup(scan, groupId, updates);
    case 'task':
      if (!groupId || !taskId) throw new Error('groupId and taskId are required');
      return getScanWithUpdatedTask(scan, groupId, taskId, updates);
    case 'option':
      if (!groupId || !taskId || !optionId) throw new Error('groupId, taskId and optionId are required');
      return getScanWithUpdatedOption(scan, groupId, taskId, optionId, updates);
    case 'lineItem':
      if (!groupId || !taskId || !optionId || !lineItemId) throw new Error('groupId, taskId, optionId and lineItemId are required');
      return getScanWithUpdatedLineItem(scan, groupId, taskId, optionId, lineItemId, updates);
    default:
      throw new Error(`Invalid scan part type: ${getScanPartType(groupId, taskId, optionId, lineItemId)}`);
  }
};
